import { useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import CustomButton from "shared/components/common/customButton";
import CustomInput from "shared/components/common/customInput";
import { Icons } from "assets";
import { toastMessage } from "shared/components/common/toast";
import styles from "./style.module.scss";
import classNames from "classnames";
import CustomFileUpload from "shared/components/common/customFileUpload";
import CustomSelect from "shared/components/common/customSelect";
import TestimonialListings from "shared/components/dashboard/dashboardListings/testimonialListings";
import CustomTextArea from "shared/components/common/customTextArea";
import {
  CreateTestimonialAnonymousService,
  UpdateTestimonialAnonymousService,
} from "shared/services/testimonials";

const DashboardTestimonials = () => {
  const [createMode, setCreateMode] = useState(false);
  const [testimonialId, setTestimonialId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<string | null>(null);

  const validationSchema = Yup.object({
    text: Yup.string().required("Testimonial text is required"),
    user_type: Yup.string().required("Select user type"),
    name: Yup.mixed().required("User name is required"),
    image: Yup.mixed().required("Image is required"),
  });

  const formik = useFormik({
    initialValues: {
      text: "",
      user_type: "",
      name: "",
      image: null as File | null,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const formData = new FormData();

        formData.append("text", values.text);
        formData.append("user_type", values.user_type);
        formData.append("name", values.name);

        if (values.image) {
          formData.append("image", values.image);
        }

        if (testimonialId) {
          const payload = {
            text: formik.values.text,
            user_type: formik.values.user_type,
            name: formik.values.name,
          };
          await UpdateTestimonialAnonymousService(testimonialId, payload);
          toastMessage("success", "Testimonial updated successfully!");
        } else {
          await CreateTestimonialAnonymousService(formData);
          toastMessage("success", "Testimonial created successfully!");
        }
        formik.resetForm();
        setCreateMode(false);
        setUploadedFile(null);
      } catch (error) {
        console.error("Error during Testimonial creation:", error);
        toastMessage("error", "Failed to create Testimonial.");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleCreateTestimonial = () => {
    setCreateMode(true);
    formik.resetForm();
  };

  const handleCancel = () => {
    setCreateMode(false);
    setTestimonialId(null);
  };

  const handleFileChange = (file: File | null) => {
    if (file) {
      formik.setFieldValue("image", file);
      setUploadedFile(URL.createObjectURL(file));
    }
  };

  // @ts-ignore
  const handleUpdate = async (data: {
    id: number;
    text: string;
    name: string;
    user_type: string;
    image: { fullpath: string | null };
  }) => {
    try {
      setCreateMode(true);
      setTestimonialId(data.id);

      const selectedType = userTypeOptions.find(
        (option) => option.value === data.user_type
      );

      formik.setValues({
        text: data.text || "",
        name: data.name || "",
        user_type: selectedType?.value || "",
        image: null,
      });

      setUploadedFile(data.image?.fullpath || null);
    } catch (error) {
      console.error(
        "Error while fetching Testimonial details for update:",
        error
      );
      toastMessage("error", "Failed to fetch Testimonial details for update.");
    }
  };

  const userTypeOptions = [
    { id: 0, value: "seller", title: "Seller" },
    { id: 1, value: "buyer", title: "Buyer" },
  ];

  return (
    <div className={classNames(styles.sectionContainer)}>
      {createMode ? (
        <>
          <div>
            <CustomButton
              Icon={Icons.ArrowLeft}
              IconDirection="left"
              iconStyle="mr-2"
              title="Back"
              containerStyle="outlined-btn-primary mb-3 px-2"
              onClick={handleCancel}
            />
          </div>
          <form
            onSubmit={formik.handleSubmit}
            className={classNames(styles.body, "grid grid-cols-12 gap-2")}
          >
            <div className="col-span-6">
              <CustomSelect
                options={userTypeOptions}
                value={formik.values.user_type}
                label="User Type"
                onChange={(selectedOption) => {
                  formik.setFieldValue("user_type", selectedOption.value);
                }}
                error={
                  formik.touched.user_type && formik.errors.user_type
                    ? formik.errors.user_type
                    : ""
                }
              />
            </div>
            <div className="col-span-6">
              <CustomInput
                required
                label="Name"
                placeholder="Enter User name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.name && formik.errors.name
                    ? formik.errors.name
                    : ""
                }
              />
            </div>{" "}
            <div className="col-span-12">
              <CustomTextArea
                required
                value={formik.values.text}
                label="Text"
                placeholder="Enter text here"
                onChange={(e) => formik.setFieldValue("text", e.target.value)}
                error={
                  formik.touched.text && formik.errors.text
                    ? formik.errors.text
                    : ""
                }
              />
            </div>
            <div className="col-span-12">
              <CustomFileUpload
                label="Upload Image"
                required
                defaultImage={uploadedFile}
                // @ts-ignore
                onFileChange={handleFileChange}
              />
            </div>
            <div className="flex justify-end mt-3 w-full col-span-12">
              <CustomButton
                type="submit"
                loading={loading}
                title={testimonialId ? "Update Testimonial" : "Add Testimonial"}
              />
            </div>
          </form>
        </>
      ) : (
        <div className={classNames(styles.header)}>
          <div className="ms-auto w-fit">
            <CustomButton
              onClick={handleCreateTestimonial}
              title="Add Testimonial"
            />
          </div>
          <div className="mt-4">
            <TestimonialListings handleUpdate={handleUpdate} />
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardTestimonials;
